import { isEmpty } from 'lodash';
import { useCallback } from 'react';
import styled from 'styled-components';
import { AiIndicator } from './button';
import { AiInputValue } from './types';

const Indicator = styled(AiIndicator)``;
const Wrapper = styled.div`
  position: relative;
  width: 100%;

  ${Indicator} {
    position: absolute;
    right: 14px;
    top: 14px;
    z-index: 1;
  }
`;

interface Props {
  value?: AiInputValue;
  onChange?: (value: AiInputValue) => void;
  regenerate?: () => Promise<string>;
}
type TOnChangeHandler<TValue> =
  | ((value: TValue) => void)
  | React.ChangeEventHandler;
export const withAiInput = <
  TValue extends string,
  TOnChangeHanlder extends TOnChangeHandler<TValue>,
  P extends { value?: TValue; onChange?: TOnChangeHanlder },
>(
  Component: React.ComponentType<P>,
) => {
  return function AiInputWrapper({
    value,
    onChange,
    regenerate,
    ...props
  }: Omit<P, 'value' | 'onChange'> & Props) {
    const aiGenerated =
      value != null && !value.editedByUser && !isEmpty(value.content);
    const content = value?.content;

    const onContentChange = useCallback(
      (event: TOnChangeHanlder) => {
        const value =
          typeof event === 'object' && 'target' in event
            ? (
                event as React.ChangeEvent<
                  HTMLInputElement | HTMLTextAreaElement
                >
              ).target.value
            : event;

        onChange?.({
          content: value as TValue,
          editedByUser: true,
        });
      },
      [onChange],
    );

    const regenerateContent = useCallback(() => {
      if (regenerate != null) {
        return regenerate().then((newValue) =>
          onChange?.({
            content: newValue,
            editedByUser: false,
          }),
        );
      }

      return Promise.resolve();
    }, [onChange, regenerate]);

    return (
      <Wrapper>
        <Indicator
          aiGenerated={aiGenerated}
          regenerate={regenerateContent}
          size="xxs"
        />
        <Component
          {...(props as P)}
          value={content}
          onChange={onContentChange}
          aiGenerated={aiGenerated}
        />
      </Wrapper>
    );
  };
};

export const isValidAiInput = (value: AiInputValue) => {
  return !isEmpty(value.content?.trim());
};
export const validateAiInput = (requiredMessage: string) => {
  return (_: any, value: AiInputValue) => {
    if (!isValidAiInput(value)) {
      return Promise.reject(new Error(requiredMessage));
    }

    return Promise.resolve();
  };
};
